import type {
    Article,
    FaqItem,
    NavLink,
    ProcessStep,
    Project,
    Service,
    Testimonial,
    Touchpoint
} from '@/types'

import { tiles } from '@/constants/images'

export const img = (name: string): string => `/images/${name}`

export const studio = {
    name: 'Astra',
    tagline: 'Brand identity, art direction and the quiet details that make a brand feel inevitable.',
    addressLabel: 'Studio',
    addressLines: ['27, Rue de Charonne.', '75011. Paris'],
    addressInline: '27, Rue de Charonne. 75011. Paris',
    phone: '+33 1 84 60 27 14',
    phoneHref: 'tel:+33184602714',
    email: 'hello@astra.studio',
    emailHref: 'mailto:hello@astra.studio',
    maps: 'https://www.google.com/maps/search/?api=1&query=27+Rue+de+Charonne+75011+Paris',
    mark: img('oblique-mark.svg'),
    exterior: tiles.arc.interior,
    enquiryImage: tiles.noma.entrance,
    social: [
        { label: 'Instagram', href: 'https://www.instagram.com/' },
        { label: 'Behance', href: 'https://www.behance.net/' },
        { label: 'LinkedIn', href: 'https://www.linkedin.com/' }
    ],
    legal: [
        { label: '404', href: '/404' },
        { label: 'Privacy Policy', href: '/privacy-policy' },
        { label: 'Terms and Conditions', href: '/terms-and-conditions' }
    ],
    copyright: '© 2016-2026 Astra Studio. All Rights Reserved.'
}

export const navLinks: NavLink[] = [
    { label: 'Home', href: '/', index: '01' },
    { label: 'Work', href: '/work', index: '02' },
    { label: 'Journal', href: '/journal', index: '03' },
    { label: 'Contact', href: '/contact', index: '04' }
]

export const heroImages = {
    first: tiles.aura.campaign,
    second: tiles.vanta.campaign
}

export const heroMarquee = 'Thanks for visiting the studio'

/* -------------------------------------------------------------- 01 Philosophy */

export const philosophy = {
    index: '01',
    label: 'Philosophy',
    next: '#selected-work',
    headline: ['Made', 'To', 'Last'],
    image: tiles.arc.wall,
    body: 'We design identities the way good buildings are made: from one clear idea, in honest materials, with nothing added for show. The result should feel like it was always there.',
    ticker: 'Selected Work'
}

/* ------------------------------------------------------------ 02 Selected Work */

export const projects: Project[] = [
    {
        slug: 'aura',
        name: 'AURA',
        sector: 'Luxury Skincare',
        year: '2025',
        summary: 'A skincare house built on stone, leaf and a very quiet green.',
        cover: tiles.aura.trio,
        hero: tiles.aura.campaign,
        disciplines: ['Identity', 'Packaging', 'Art Direction'],
        gallery: [tiles.aura.portrait, tiles.aura.duo, tiles.aura.card, tiles.aura.bag, tiles.aura.editorial, tiles.aura.carton]
    },
    {
        slug: 'kin',
        name: 'KIN',
        sector: 'Specialty Coffee',
        year: '2024',
        summary: 'A roastery identity that reads from across the street and up close on the bag.',
        cover: tiles.kin.cover,
        hero: tiles.kin.campaign,
        disciplines: ['Identity', 'Packaging', 'Signage'],
        gallery: [tiles.kin.wordmark, tiles.kin.cup, tiles.kin.sign, tiles.kin.menu, tiles.kin.tote, tiles.kin.pairing]
    },
    {
        slug: 'vanta',
        name: 'VANTA',
        sector: 'Fashion',
        year: '2025',
        summary: 'Black on black for a label about modern essentials.',
        cover: tiles.vanta.label,
        hero: tiles.vanta.campaign,
        disciplines: ['Identity', 'Art Direction', 'Typography'],
        gallery: [tiles.vanta.figure, tiles.vanta.magazine, tiles.vanta.hoodie, tiles.vanta.poster, tiles.vanta.sign, tiles.vanta.wordmark]
    },
    {
        slug: 'arc',
        name: 'ARC',
        sector: 'Architecture Studio',
        year: '2024',
        summary: 'A single line weight for a practice that works in concrete and light.',
        cover: tiles.arc.house,
        hero: tiles.arc.campaign,
        disciplines: ['Logo Design', 'Typography', 'Digital'],
        gallery: [tiles.arc.cards, tiles.arc.monograph, tiles.arc.wall, tiles.arc.interior, tiles.arc.tablet, tiles.arc.wordmark]
    },
    {
        slug: 'noma',
        name: 'NOMA',
        sector: 'Premium Restaurant',
        year: '2025',
        summary: 'Warm, tactile and understated, like the room and the food.',
        cover: tiles.noma.plate,
        hero: tiles.noma.campaign,
        disciplines: ['Identity', 'Print', 'Art Direction'],
        gallery: [tiles.noma.book, tiles.noma.menu, tiles.noma.linen, tiles.noma.entrance, tiles.noma.plate, tiles.noma.dish]
    },
    {
        slug: 'mono',
        name: 'MONO',
        sector: 'Technology',
        year: '2025',
        summary: 'Technology for a calmer life, designed to disappear into the home.',
        cover: tiles.mono.speaker,
        hero: tiles.mono.campaign,
        disciplines: ['Identity', 'Packaging', 'Digital'],
        gallery: [tiles.mono.box, tiles.mono.phones, tiles.mono.device, tiles.mono.wall, tiles.mono.wordmark, tiles.mono.speaker]
    }
]

export const workSection = {
    index: '02',
    label: 'Selected Work',
    next: '#services',
    headline: 'Six brands, each drawn from a single idea and carried through every surface it touches.',
    body: 'Identity, packaging, space and screen, designed as one system and built to stay consistent for years.',
    cta: { label: 'All Projects', href: '/work' }
}

/* ----------------------------------------------------------------- 03 Services */

export const services: Service[] = [
    {
        slug: 'brand-identity',
        index: '01',
        title: 'Brand Identity',
        image: tiles.mono.wall,
        intro: 'Strategy, naming and the core idea a brand is built on, agreed in plain words before anything is drawn.',
        capabilities: [
            { title: 'Brand Strategy', slug: 'brand-strategy', summary: 'Who the brand is for, what it stands for and how it should sound, written down in a page anyone in the company can read.' },
            { title: 'Naming', slug: 'naming', summary: 'Names that are short, ownable and easy to say in every market you sell in, checked before you fall in love with them.' },
            { title: 'Identity Systems', slug: 'identity-systems', summary: 'Logo, type, colour and layout designed together as one system, so every new piece looks like it belongs.' }
        ]
    },
    {
        slug: 'visual-identity',
        index: '02',
        title: 'Visual Identity',
        image: tiles.aura.editorial,
        intro: 'Colour, imagery, layout and material, set out as a system a team can actually use.',
        capabilities: [
            { title: 'Colour and Material', slug: 'colour-and-material', summary: 'A palette tested on real paper, fabric and screens, with the finishes that make it hold up in daylight.' },
            { title: 'Layout Systems', slug: 'layout-systems', summary: 'Grids and templates that keep every page, post and packshot consistent without making them identical.' },
            { title: 'Brand Guidelines', slug: 'brand-guidelines', summary: 'Short, visual guidelines your team will open, with the reasoning behind each rule so it survives new hires.' }
        ]
    },
    {
        slug: 'art-direction',
        index: '03',
        title: 'Art Direction',
        image: tiles.vanta.magazine,
        intro: 'Campaigns, photography and film directed so every image sounds like the same brand.',
        capabilities: [
            { title: 'Campaign Direction', slug: 'campaign-direction', summary: 'One clear visual idea per campaign, carried from the first shoot to the last banner.' },
            { title: 'Photography', slug: 'photography', summary: 'Product, portrait and space photography directed on set, lit and styled to the identity.' },
            { title: 'Set and Styling', slug: 'set-and-styling', summary: 'Materials, props and locations chosen to say something about the brand before any word is read.' }
        ]
    },
    {
        slug: 'logo-design',
        index: '04',
        title: 'Logo Design',
        image: tiles.arc.cards,
        intro: 'Wordmarks and symbols drawn by hand, refined optically and tested at every size they will ever appear.',
        capabilities: [
            { title: 'Wordmarks', slug: 'wordmarks', summary: 'Custom lettered wordmarks, spaced and balanced by eye rather than set straight from a font.' },
            { title: 'Symbols and Monograms', slug: 'symbols-and-monograms', summary: 'Marks that work at favicon size and on a building, drawn to sit comfortably beside the wordmark.' },
            { title: 'Logo Refinement', slug: 'logo-refinement', summary: 'Careful redrawing of an existing logo, keeping what people recognise and fixing what never quite worked.' }
        ]
    },
    {
        slug: 'typography',
        index: '05',
        title: 'Typography',
        image: tiles.vanta.sheet,
        intro: 'Typefaces chosen, paired and sometimes drawn from scratch, with clear rules for setting them well.',
        capabilities: [
            { title: 'Type Selection', slug: 'type-selection', summary: 'Families chosen for voice, legibility and licensing, then paired so each one has a clear job.' },
            { title: 'Custom Lettering', slug: 'custom-lettering', summary: 'Display letters drawn for a single brand, from a headline cut to a complete wordmark alphabet.' },
            { title: 'Typographic Systems', slug: 'typographic-systems', summary: 'Sizes, spacing and hierarchy defined once, so print and screen read as the same voice.' }
        ]
    },
    {
        slug: 'packaging',
        index: '06',
        title: 'Packaging',
        image: tiles.kin.packaging,
        intro: 'Structure, print and finish designed together, so the object feels as considered as the brand.',
        capabilities: [
            { title: 'Structural Packaging', slug: 'structural-packaging', summary: 'Cartons, bags and boxes shaped around the product and the moment it is opened.' },
            { title: 'Labels and Finishes', slug: 'labels-and-finishes', summary: 'Label hierarchy, papers, foils and embossing specified down to the last millimetre.' },
            { title: 'Print Production', slug: 'print-production', summary: 'Proofs, press checks and supplier management, so what arrives matches what was approved.' }
        ]
    },
    {
        slug: 'digital-branding',
        index: '07',
        title: 'Digital Branding',
        image: tiles.arc.tablet,
        intro: 'Websites, product interfaces and motion that carry the identity onto every screen.',
        capabilities: [
            { title: 'Websites', slug: 'websites', summary: 'Editorial websites designed around the work, fast to load and simple for your team to update.' },
            { title: 'Interface Design', slug: 'interface-design', summary: 'App and product interfaces that feel like the brand while staying clear enough to use every day.' },
            { title: 'Motion and Social', slug: 'motion-and-social', summary: 'Logo animation, motion rules and social templates, so the brand moves the same way everywhere.' }
        ]
    }
]

export const servicesSection = {
    index: '03',
    label: 'Services',
    next: '#index',
    headline: 'An independent studio for the whole of a brand, from the first idea to the last detail.',
    cta: 'Learn More'
}

/* -------------------------------------------------------------------- 04 Index */

export const indexSection = {
    index: '04',
    label: 'Index',
    next: '#process',
    headline: ['Index', 'Every project, from the first mark to the last detail.'],
    cta: { label: 'View All Work', href: '/work' }
}

/* ------------------------------------------------------------------ 05 Process */

export const processSteps: ProcessStep[] = [
    {
        index: '01',
        title: ['Listen', 'Closely'],
        caption: 'Workshops, research and an honest audit',
        image: tiles.vanta.poster
    },
    {
        index: '02',
        title: ['Draw', 'the', 'System'],
        caption: 'One idea carried into logo, type and colour',
        image: tiles.kin.tote
    },
    {
        index: '03',
        title: ['Build', 'and Launch'],
        caption: 'Every touchpoint produced and handed over',
        image: tiles.aura.bag
    }
]

export const processSection = {
    index: '05',
    label: 'Process',
    next: '#touchpoints',
    headline: ['How', 'Every identity moves through 03 stages']
}

/* -------------------------------------------------------------- 06 Touchpoints */

export const touchpoints: Touchpoint[] = [
    { slug: 'packaging', title: ['Packaging'], image: tiles.aura.carton },
    { slug: 'stationery-and-print', title: ['Stationery', 'and Print'], image: tiles.aura.card },
    { slug: 'signage-and-space', title: ['Signage', 'and Space'], image: tiles.kin.sign },
    { slug: 'editorial', title: ['Editorial'], image: tiles.arc.monograph },
    { slug: 'digital-product', title: ['Digital', 'Product'], image: tiles.mono.phones },
    { slug: 'apparel-and-goods', title: ['Apparel', 'and Goods'], image: tiles.vanta.hoodie }
]

export const touchpointsSection = {
    index: '06',
    label: 'Touchpoints',
    next: '#testimonials',
    body: 'Six places a brand has to hold together, from the shelf to the screen to the front door.'
}

export const workPage = {
    headline: { roman: 'Selected', italic: 'Work' },
    summary: 'Six identities for skincare, coffee, fashion, architecture, food and technology.',
    railLabel: 'Browse the projects'
}

export const journalPage = {
    headline: 'Journal',
    summary: 'Notes from the studio on type, colour, materials and the decisions behind the work.'
}

/* ------------------------------------------------------------ 07 Testimonials */

export const testimonials: Testimonial[] = [
    {
        name: 'Élise Varenne',
        role: 'Founder, AURA',
        quote: 'They understood the product before they understood the brief. Two years on, the packaging still stops people in the shop, and we have never had to explain it.',
        project: 'AURA, Luxury Skincare',
        image: tiles.aura.duo
    },
    {
        name: 'Tomás Reyes',
        role: 'Co-founder, KIN',
        quote: 'The label system alone changed how we sell. Customers read the bags now, and our baristas stopped having to translate the menu.',
        project: 'KIN, Specialty Coffee',
        image: tiles.kin.cup
    },
    {
        name: 'Hanna Lindqvist',
        role: 'Principal, ARC',
        quote: 'Architects are difficult clients. They listened, pushed back when we were wrong, and gave us a mark that looks like it came off our own drawing boards.',
        project: 'ARC, Architecture Studio',
        image: tiles.arc.cards
    },
    {
        name: 'Luca Moretti',
        role: 'Chef and Owner, NOMA',
        quote: 'The menus, the plates, the linen. Guests photograph the table before the food arrives, which I have decided to take as a compliment.',
        project: 'NOMA, Premium Restaurant',
        image: tiles.noma.plate
    }
]

export const testimonialSection = {
    index: '07',
    label: 'Testimonials',
    next: '#working-together',
    headline: 'A few words from the founders we have worked with.'
}

/* -------------------------------------------------------- 08 Working Together */

export const engagementSection = {
    index: '08',
    label: 'Working Together',
    next: '#faq',
    headline: ['Clear Scope', 'Fixed Fees'],
    blocks: [
        {
            title: 'Identity Projects',
            body: 'Most identities run twelve to sixteen weeks on a fixed fee, agreed in writing before we start. You always know what is included and when it arrives.'
        },
        {
            title: 'Ongoing Partnerships',
            body: 'After launch, many clients keep us on a monthly retainer for campaigns, packaging and new products, so the brand stays consistent as it grows.'
        }
    ],
    brandsLabel: 'Brands we have built',
    brands: projects.map((project) => project.name)
}

/* --------------------------------------------------------------------- 09 FAQ */

export const faqs: FaqItem[] = [
    {
        question: 'How long does a brand identity take?',
        answer: 'Most identities take twelve to sixteen weeks from the first workshop to final files. Packaging, signage and websites add time, and we plan all of it up front so nothing surprises you.'
    },
    {
        question: 'What does a project cost?',
        answer: 'Every project is quoted as a fixed fee after a first conversation. Identities usually start around twenty thousand euros, and the proposal lists exactly what is included.'
    },
    {
        question: 'Do you work with new businesses?',
        answer: 'Often. Several of the brands in our work started with us before they had a product on a shelf. We only ask that the founders are closely involved.'
    },
    {
        question: 'What do we receive at the end?',
        answer: 'Master logo files, colour and type specifications, templates, print-ready artwork and a set of brand guidelines. You own everything we make for you.'
    },
    {
        question: 'Can you work with our in-house team?',
        answer: 'Yes. We regularly design the system and hand it to an internal team, then stay on call for reviews and the occasional difficult piece.'
    },
    {
        question: 'Do you only work in Paris?',
        answer: 'No. The studio is in Paris, but most of our clients are elsewhere in Europe and further afield. We travel for workshops and shoots, and work remotely in between.'
    }
]

export const faqSection = {
    index: '09',
    label: 'FAQ',
    next: '#journal',
    headline: 'FAQ',
    cta: { label: 'All FAQs', href: '/faq' }
}

/* ----------------------------------------------------------------- 10 Journal */

export const articles: Article[] = [
    {
        slug: 'the-green-that-reads-as-black',
        title: 'The Green That Reads as Black: Choosing a Colour for AURA',
        image: tiles.aura.campaign
    },
    {
        slug: 'designing-a-label-people-actually-read',
        title: 'Designing a Coffee Label People Actually Read',
        image: tiles.kin.pairing
    },
    {
        slug: 'one-line-weight-drawing-the-arc-wordmark',
        title: 'One Line Weight: Drawing the ARC Wordmark',
        image: tiles.arc.campaign
    },
    {
        slug: 'restraint-is-a-decision-not-a-style',
        title: 'Restraint Is a Decision, Not a Style',
        image: tiles.vanta.sign
    },
    {
        slug: 'the-table-is-the-brand',
        title: 'The Table Is the Brand: Notes from NOMA',
        image: tiles.noma.campaign
    },
    {
        slug: 'designing-for-a-quieter-screen',
        title: 'Designing for a Quieter Screen',
        image: tiles.mono.campaign
    }
]

export const journalSection = {
    index: '10',
    label: 'Journal',
    next: '#start-a-project',
    headline: 'Notes from the studio on the decisions behind the work.',
    cta: { label: 'All Journal', href: '/journal' }
}

/* -------------------------------------------------------------- Enquiry CTA */

export const enquirySection = {
    label: 'Start A Project',
    headline: ['Have A', 'Brand', 'To Build?'],
    body: 'Tell us about it. We reply to every enquiry within two working days.',
    primary: { label: 'Start a Project', href: '/contact' },
    secondary: { label: 'Email Us', href: studio.emailHref },
    chips: ['Small, senior team', 'Fixed-fee proposals', 'Reply within 48 hours']
}

export const visitSection = {
    label: 'Visit Us',
    headline: 'Come by for coffee and a proper look at the work on the wall.',
    amenities: ['Open studio every Thursday', 'Meetings by appointment', 'Five minutes from Bastille', 'Projects worldwide, remotely'],
    cta: { label: 'Open In Google Maps', href: studio.maps }
}

export const footerMarquee = 'Brands built slowly, and meant to last'
